import {
  Box,
  type BoxProps,
  CircularProgress,
  Icon,
  Stack,
  TextField,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useSearchBox } from '@/components/shared/SearchBox/useSearchBox';
import SearchResultsList from './SearchResultsList';
import type { SearchResult } from './types';

interface SearchBoxProps {
  placeholder?: string;
  value?: string;
  selectedResult?: SearchResult | null;
  onSelect?: (result: SearchResult | null) => void;
  onChange?: (search: string) => void;
  searchResults?: SearchResult[];
  sx?: BoxProps['sx'];
  isLoading?: boolean;
  toggleResultsOnFocus?: boolean;
  error?: string;
  hideSearchIconOnSearch?: boolean;
  simpleSelectedResultView?: boolean;
  onClearSelection?: () => void;
}

const SearchBox: React.FC<SearchBoxProps> = ({
  placeholder = 'Search',
  value,
  selectedResult: controlledSelected,
  onSelect,
  onChange,
  searchResults,
  sx = [],
  isLoading = false,
  toggleResultsOnFocus = false,
  error,
  hideSearchIconOnSearch = false,
  simpleSelectedResultView = false,
  onClearSelection,
}) => {
  const search = useSearchBox({
    value,
    selectedResult: controlledSelected,
    onSelect,
    onChange,
    onClearSelection,
    searchResults,
  });

  const {
    displayValue,
    selectedResult,
    highlightedIndex,
    openSearchResults,
    handleSearchChange,
    handleSelect,
    handleClearSelected,
    handleKeyDown: handleKeyDownBase,
    handleFocus,
    handleBlur,
    setOpenSearchResults,
    textFieldRef,
  } = search;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    handleKeyDownBase(e);
  };

  return (
    <Box
      sx={[
        {
          position: 'relative',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      data-testid='branch-search-input'
    >
      <TextField
        variant='outlined'
        fullWidth
        placeholder={selectedResult ? '' : placeholder}
        value={displayValue}
        inputRef={textFieldRef}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
        error={!!error}
        showClearButton
        data-testid='search-box'
        helperText={
          error ? (
            <Stack
              direction='row'
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
              component={'span'}
            >
              <Icon
                iconName='triangle-exclamation-solid'
                sx={{
                  color: 'error.main',
                  fontSize: 20,
                }}
              />
              <Typography
                variant='view-label-secondary'
                sx={{
                  color: 'error.main',
                }}
              >
                {error}
              </Typography>
            </Stack>
          ) : null
        }
        onClick={() => {
          if (toggleResultsOnFocus) {
            setOpenSearchResults(true);
          }
          handleFocus();
        }}
        onBlur={() => {
          if (toggleResultsOnFocus) {
            setOpenSearchResults(false);
          }
          handleBlur();
        }}
        slotProps={{
          input: {
            startAdornment: (
              <Stack
                direction='row'
                spacing={1.5}
                sx={{
                  alignItems: 'center',
                }}
              >
                {!(
                  hideSearchIconOnSearch &&
                  (displayValue || selectedResult)
                ) && (
                  <Box
                    sx={{
                      pl: 2,
                      backgroundColor: 'inherit',
                    }}
                  >
                    <Icon
                      iconName='magnifying-glass-regular'
                      standalone
                      className='search-icon'
                      sx={{
                        color: 'primary.600',
                      }}
                    />
                  </Box>
                )}
                {selectedResult &&
                  (simpleSelectedResultView ? (
                    <Typography
                      variant='view-label-primary'
                      sx={[
                        {
                          whiteSpace: 'nowrap',
                        },
                        hideSearchIconOnSearch && {
                          pl: 1.5,
                        },
                      ]}
                      data-testid='selected-result-title'
                    >
                      {selectedResult.title}
                    </Typography>
                  ) : (
                    <Stack
                      direction='row'
                      spacing={1}
                      sx={[
                        {
                          backgroundColor: 'primary.100',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          justifyContent: 'center',
                          alignItems: 'center',
                        },
                        hideSearchIconOnSearch && {
                          ml: '12px !important',
                        },
                      ]}
                      className='selected-result'
                    >
                      <Typography
                        variant='view-label-primary'
                        sx={{
                          whiteSpace: 'nowrap',
                          color: 'primary.400',
                        }}
                      >
                        {selectedResult.title}
                      </Typography>
                      <Box onClick={handleClearSelected}>
                        <Icon
                          iconName='xmark-regular'
                          sx={{
                            color: 'primary.400',
                            cursor: 'pointer',
                            width: '0.75rem',
                          }}
                        />
                      </Box>
                    </Stack>
                  ))}
              </Stack>
            ),
            endAdornment: (
              <Stack
                direction='row'
                spacing={1}
                sx={{
                  alignItems: 'center',
                  pr: 2,
                }}
              >
                {(displayValue ||
                  (selectedResult && simpleSelectedResultView)) && (
                  <Box
                    sx={{
                      backgroundColor: 'inherit',
                    }}
                    data-testid='search-box-clear-button'
                    onClick={() => {
                      handleClearSelected();
                    }}
                  >
                    <Icon
                      iconName='xmark-regular'
                      standalone
                      sx={{
                        cursor: 'pointer',
                        fontSize: 20,
                      }}
                    />
                  </Box>
                )}
                {isLoading && (
                  <Box data-testid='loading-indicator'>
                    <CircularProgress size={25} />
                  </Box>
                )}
              </Stack>
            ),
            readOnly: !!selectedResult,
          },
        }}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            '& .MuiInputBase-input': {
              // trick to remove autofill styles
              '&:is(:-webkit-autofill,:-webkit-autofill-focus)': {
                transition:
                  'background-color 0s 600000s, color 0s 600000s !important;',
              },
            },
            '&:hover': {
              borderColor: 'grey.200',
              backgroundColor: (theme) =>
                `${theme.palette.primary[100]} !important`,
              '& .selected-result': {
                backgroundColor: 'primary.400',
                '& .MuiTypography-root, .MuiSvgIcon-root': {
                  color: 'white',
                },
              },
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'grey.200',
            },
            '&.MuiInputBase-readOnly:not(.Mui-visuallyEnabled)': {
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'primary.100',
              },
            },
          },
        }}
      />
      {searchResults && (
        <SearchResultsList
          results={searchResults}
          onSelect={handleSelect}
          open={displayValue.length > 0 || openSearchResults}
          highlightedIndex={highlightedIndex}
        />
      )}
    </Box>
  );
};

export default SearchBox;
